import * as React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Combobox } from '@/components/ui/combobox';
import { useTransitionOffcut } from '@/hooks/offcuts/use-transition-offcut';
import { useToast } from '@/hooks/use-toast';
import type { OffcutItemDto } from '@cutlyft/zod-schemas';

const STATUS_OPTIONS = [
  { value: 'CONSUMED', label: 'Consumed' },
  { value: 'SCRAPPED', label: 'Scrapped' },
];

interface TransitionModalProps {
  offcut: OffcutItemDto;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TransitionModal({ offcut, open, onOpenChange }: TransitionModalProps) {
  const { toast } = useToast();
  const transition = useTransitionOffcut();

  const [toStatus, setToStatus] = React.useState<'CONSUMED' | 'SCRAPPED'>('CONSUMED');
  const [reason, setReason] = React.useState('');
  const [note, setNote] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    transition.mutate(
      { id: offcut.id, body: { toStatus, reason: reason || undefined, note: note || undefined } },
      {
        onSuccess: () => {
          toast({ title: `Offcut #${offcut.id} marked as ${toStatus.toLowerCase()}` });
          onOpenChange(false);
        },
        onError: () => {
          toast({ variant: 'destructive', title: 'Failed to transition offcut' });
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Transition Offcut #{offcut.id}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <p className="text-sm text-fg-muted">
            Length: <span className="font-mono">{offcut.lengthMm} mm</span>
          </p>

          <div className="space-y-1.5">
            <Label>New Status</Label>
            <Combobox
              options={STATUS_OPTIONS}
              value={toStatus}
              onValueChange={(v) => v && setToStatus(v as 'CONSUMED' | 'SCRAPPED')}
              placeholder="Select status"
              searchPlaceholder="Search status..."
              clearable={false}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="reason">Reason (optional)</Label>
            <Input
              id="reason"
              placeholder="e.g. used in job #123"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              maxLength={64}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="note">Note (optional)</Label>
            <Input
              id="note"
              placeholder="Audit trail note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={transition.isPending}>
              {transition.isPending ? 'Saving…' : 'Confirm'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
